/**
 * The Ledger Button. Import buttons from here, never from `@chakra-ui/react`
 * (a Biome rule enforces it).
 *
 * A drop-in for Chakra's `Button`: every Chakra prop is forwarded, and the
 * look comes from the `button` recipe (`src/theme/recipes/button.ts`). On top
 * of Chakra it adds:
 *
 * - the Ledger variant names: `primary` (accent fill), `secondary` (surface,
 *   1px border), `danger` (err fill, dialogs only) and `danger-ghost`, beside
 *   Chakra's own `solid`, `outline`, `ghost`, `subtle`, `surface`, `plain`;
 * - `busy`: a spinner takes the leading icon's place, the label stays and the
 *   width does not move; the button stops taking presses and says it is busy;
 * - `disabledReason`: a disabled button that explains itself. It stays
 *   focusable and hoverable (`aria-disabled`, not `disabled`) so the reason can
 *   show in a tooltip, and it still refuses presses and form submits.
 *
 * Spec: specs/design-system/ledger-primitives.feature
 */
import {
  Button as ChakraButton,
  type ButtonProps as ChakraButtonProps,
  chakra,
} from "@chakra-ui/react";
import * as React from "react";
import { useHeldWidth } from "~/hooks/useHeldWidth";
import { Spinner } from "./spinner";
import { Tooltip, useTooltipOverride } from "./tooltip";

export type LedgerButtonVariant =
  | "primary"
  | "secondary"
  | "danger"
  | "danger-ghost";

export interface ButtonProps extends Omit<ChakraButtonProps, "variant"> {
  variant?: ChakraButtonProps["variant"] | LedgerButtonVariant;
  /** Work in progress: a spinner replaces the icon; the label and width stay. */
  busy?: boolean;
  /**
   * Why the button is disabled, shown in a tooltip. Setting it disables the
   * button; leave it unset (or `disabled` false) for an enabled one.
   */
  disabledReason?: React.ReactNode;
}

/** The Chakra variant and palette a Ledger variant name stands for. */
export function resolveButtonVariant({
  variant,
  colorPalette,
}: {
  variant: ButtonProps["variant"];
  colorPalette: ButtonProps["colorPalette"];
}): Pick<ChakraButtonProps, "variant" | "colorPalette"> & {
  dangerGhost: boolean;
} {
  switch (variant) {
    case "primary":
      return { variant: "solid", colorPalette: "accent", dangerGhost: false };
    case "secondary":
      return { variant: "outline", colorPalette, dangerGhost: false };
    case "danger":
      return { variant: "solid", colorPalette: "err", dangerGhost: false };
    case "danger-ghost":
      return { variant: "ghost", colorPalette: "err", dangerGhost: true };
    default:
      return { variant, colorPalette, dangerGhost: false };
  }
}

/** Whether a node carries text a person reads (a label, not a glyph). */
function hasText(node: React.ReactNode): boolean {
  if (typeof node === "string") return node.trim() !== "";
  if (typeof node === "number") return true;
  if (Array.isArray(node)) return node.some(hasText);
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) {
    return hasText(node.props.children);
  }
  return false;
}

/**
 * Whether a child is an icon: an `<svg>`, or an element marked
 * `data-icon`, or a component element with no text inside it and no
 * children at all (a lucide or react-icons glyph). A child holding words
 * (`<span>Save</span>`, `<Text>`, `<Trans>`) is a label, never an icon.
 */
export function isIconElement(node: React.ReactNode): boolean {
  if (!React.isValidElement<Record<string, unknown>>(node)) return false;
  if (node.type === "svg" || node.props["data-icon"] !== undefined) {
    return true;
  }
  if (typeof node.type === "string") return false;
  if (node.props.children != null || hasText(node)) return false;
  // A translation component renders words from props, not children.
  return !("i18nKey" in node.props || "id" in node.props);
}

/** Splits children into a leading icon (if the first child is one) and the rest. */
function splitLeadingIcon(children: React.ReactNode): {
  icon: React.ReactNode | null;
  rest: React.ReactNode[];
} {
  const items = React.Children.toArray(children);
  const [first, ...rest] = items;
  if (first !== undefined && isIconElement(first)) {
    return { icon: first, rest };
  }
  return { icon: null, rest: items };
}

/**
 * What a busy button shows: the spinner in the leading icon's slot, the
 * label left as it is. With no icon the spinner takes the same leading slot
 * in front of the label; the button's width is held by the wrapper.
 */
export function busyChildren(children: React.ReactNode): React.ReactNode {
  const { icon, rest } = splitLeadingIcon(children);
  return (
    <>
      <Spinner size="xs" />
      {icon ? (
        rest
      ) : (
        // The spinner adds to the content, so in a width-held button the
        // label gives way with an ellipsis rather than overflow.
        <chakra.span minWidth="0" truncate>
          {rest}
        </chakra.span>
      )}
    </>
  );
}

/** The danger-ghost colours: err text, the err tint on hover. */
const DANGER_GHOST = {
  color: "err",
  _hover: { bg: "err.soft" },
} as const;

/** Blocks presses (and the form submit a press would cause). */
function refusePress(event: React.MouseEvent) {
  event.preventDefault();
  event.stopPropagation();
}

/**
 * The attributes that carry busy and explained-disabled: an explained button
 * is `aria-disabled` (still focusable, so its tooltip can show) rather than
 * `disabled`, and neither a busy nor an explained button takes a press.
 */
function stateProps({
  busy,
  explained,
  disabled,
  onClick,
  rest,
}: {
  busy: boolean;
  explained: boolean;
  disabled: ButtonProps["disabled"];
  onClick: ButtonProps["onClick"];
  rest: Omit<ButtonProps, "busy" | "disabledReason">;
}): Partial<ChakraButtonProps> & Record<`data-${string}`, string | undefined> {
  if (!busy && !explained) return { disabled, onClick };
  return {
    disabled: explained ? undefined : disabled,
    "aria-disabled": explained ? true : rest["aria-disabled"],
    "data-disabled": explained ? "" : undefined,
    "aria-busy": busy ? true : rest["aria-busy"],
    "data-busy": busy ? "" : undefined,
    onClick: refusePress,
  };
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(props, ref) {
    const {
      busy = false,
      disabledReason,
      variant,
      colorPalette,
      children,
      disabled,
      onClick,
      ...rest
    } = props;

    const { dangerGhost, ...resolved } = resolveButtonVariant({
      variant,
      colorPalette,
    });
    const explained = disabledReason != null && disabled !== false;
    // Inside another Tooltip's trigger the reason is shown by that tooltip
    // (it owns the trigger, so a tooltip of our own would never open).
    const ownRef = React.useRef<HTMLButtonElement | null>(null);
    const insideTooltip = useTooltipOverride({
      content: explained ? disabledReason : null,
      nodeRef: ownRef,
    });
    const heldWidth = useHeldWidth({ hold: busy, ref: ownRef });
    const setRef = React.useCallback(
      (node: HTMLButtonElement | null) => {
        ownRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      },
      [ref],
    );

    const button = (
      <ChakraButton
        ref={setRef}
        {...resolved}
        {...(dangerGhost ? DANGER_GHOST : {})}
        {...rest}
        {...stateProps({ busy, explained, disabled, onClick, rest })}
        {...(heldWidth !== undefined && rest.width === undefined
          ? { style: { ...rest.style, width: heldWidth } }
          : {})}
      >
        {busy && !rest.asChild ? busyChildren(children) : children}
      </ChakraButton>
    );

    if (!explained || insideTooltip) return button;
    return (
      <Tooltip content={disabledReason} openDelay={140}>
        {button}
      </Tooltip>
    );
  },
);
